import { type CSSProperties } from "react";

import clsx from "clsx";

import { useMMMDateRange } from "@features/mmm/hooks/mmm-url-state";
import { formatPercentage } from "@features/mmm/utils/formatters";
import { safeDivide } from "@north-beam/nb-common";
import { renderChevronAnimation } from "@shared/selects/utils";

import { PercentageChangeChip } from "../budget-mix-card/title-section/metric-box";
import { useShowReferencePeriod } from "../hooks/use-show-reference-period";

import { BarTooltip } from "./bar-tooltip";
import { useExpandedBreakdowns } from "./hooks/use-expanded-breakdowns";
import { type BudgetMixChartDatum } from "./segmented-chart";

const normalizePercentage = (percentage: number, maxNumber: number) =>
  safeDivide(percentage * 100, maxNumber, {
    zeroOverZero: 0,
    infOverInf: 1,
  });

export const Bar = ({
  data: { breakdownKey, breakdownValue, percentage, designElement, spend },
  historicalBudgetMixData,
  maxNumber,
  isHistorical,
  canExpand = false,
  isChild = false,
  dateRange,
}: {
  data: BudgetMixChartDatum;
  historicalBudgetMixData?: BudgetMixChartDatum;
  maxNumber: number;
  isHistorical: boolean;
  canExpand?: boolean;
  isChild?: boolean;
  dateRange: { startDate: string; endDate: string };
}) => {
  const percent = normalizePercentage(percentage, maxNumber) ?? percentage;
  const [historicalMixDateRange] = useMMMDateRange();

  const { unexpandBreakdown, expandBreakdown, isBreakdownExpanded } =
    useExpandedBreakdowns();

  const historicalPercent =
    normalizePercentage(historicalBudgetMixData?.percentage ?? 0, maxNumber) ??
    historicalBudgetMixData?.percentage ??
    0;

  const enableMmmReferencePeriod = useShowReferencePeriod();

  return (
    <div
      className={clsx("flex items-center space-x-1 py-[2.25px]", {
        "opacity-60": isBreakdownExpanded(breakdownKey, breakdownValue),
      })}
    >
      <div
        className="text-xs font-semibold px-2 py-1 rounded-full w-fit min-w-[54px] h-4 flex-center bg-v2-primary"
        style={{ border: `1px solid ${designElement.iconColor}` }}
      >
        {formatPercentage(percent)}
      </div>
      <div className="w-20 min-w-[80px] bg-cosmic-4 rounded relative">
        <div
          className={clsx("nb-stacked-bar h-5 rounded-l", {
            "rounded-r": percent >= 99.5,
          })}
          style={{
            backgroundColor: designElement.iconColor,
            width: `${percent}%`,
          }}
        ></div>
        {!isHistorical && enableMmmReferencePeriod && (
          <HistoricalChartLine
            style={{ left: `${historicalPercent}%`, height: "100%" }}
          />
        )}
      </div>
      <div
        className={clsx(
          "w-5 h-5 min-w-[20px] flex-center rounded hover:bg-cosmic-4",
          {
            "invisible pointer-event-none": isChild,
            hidden: !canExpand && !isChild,
            "cursor-pointer": !!canExpand,
          },
        )}
        onClick={() => {
          if (isBreakdownExpanded(breakdownKey, breakdownValue)) {
            unexpandBreakdown(breakdownKey, breakdownValue);
          } else {
            expandBreakdown(breakdownKey, breakdownValue);
          }
        }}
      >
        {renderChevronAnimation(
          isBreakdownExpanded(breakdownKey, breakdownValue),
          false,
          undefined,
          "ui-open:text-white text-xs",
        )}
      </div>
      {isChild ? (
        <i className="fa-regular fa-arrow-turn-down-right text-gray-1 text-2xs pr-2" />
      ) : (
        designElement.getComponent({ size: "1.5em" })
      )}
      <div className="font-normal truncate flex-grow">{breakdownValue}</div>
      <BarTooltip
        designElement={designElement}
        breakdownValue={breakdownValue}
        isHistorical={isHistorical}
        historicalPercent={historicalPercent}
        spend={spend}
        percent={percent}
        historicalBudgetMixData={historicalBudgetMixData}
        dateRange={isHistorical ? historicalMixDateRange : dateRange}
      >
        <PercentageChangeChip
          className="justify-self-end"
          isNeutral
          isHistorical={isHistorical}
          compareData={{
            metricChangePercentage: percent - historicalPercent,
            isDesiredDirection: percent >= historicalPercent,
          }}
        />
      </BarTooltip>
    </div>
  );
};

const HistoricalChartLine = ({
  style,
  className,
}: {
  style?: CSSProperties;
  className?: string;
}) => (
  <div
    className={clsx(
      "border-r border-interstellar border-dashed absolute top-0",
      className,
    )}
    style={style}
  >
    <div
      className={clsx(
        "border-l-3 border-l-transparent",
        "border-r-3 border-r-transparent",
        "border-t-3 border-t-interstellar",
        "absolute transform -translate-x-[42%]",
      )}
    ></div>
  </div>
);
